import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import {
  TEntityActions,
  TRectDropElement,
  TTimelineConfiguration,
  TTimelineIndicators,
  TTimelineModel,
  TToolbarAction,
} from './model/timeline.types';
import { TTimelineChartGroups, createTimelineChart } from './model/d3/draw';
import { addMarkers, addStripEvent, addStripOnCursor, removeCursor } from './model/d3/events/events';
import { TIMELINE_D3, TDrawModel } from './model/draw.model';
import { DRAW_MODEL_UTILS } from './model/d3/utils';
import { TUserPaginationOption } from '../listing-pagination/pagination.types';
import { TPaginationInfo, TLL_RECALIBRATE_TYPES, TTimelineDateRangeDTO, TDate } from '@monorepo/types';
import { MenuController, ModalController, PopoverController } from '@ionic/angular';

export type TToolbarActionEventEmit = [Exclude<TToolbarAction, 'SEARCH'>, Event] | ['SEARCH', Event | null];

@Component({
  selector: 'monorepo-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
})
export class TimelineComponent implements AfterViewInit, OnDestroy {
  #chartGroup: TTimelineChartGroups | null = null;
  #viewDateRange: TTimelineDateRangeDTO | null = null;
  #drawModel: TDrawModel | null = null;
  #scrollRangeListener: (() => void) | null = null;
  #timelineWrapperScrollEl: HTMLDivElement | null = null;
  #isProgrammaticScrolling = false;

  timelineView: TTimelineModel['boardView']['boardViewType'] = 'WEEKLY';
  hideLeftPanel?: boolean = undefined;

  // Setting Timeline model
  _timelineModel!: TTimelineModel;
  @Input() set timelineModel(value: TTimelineModel) {
    this._timelineModel = value;
    this.#renderTimeline(this._timelineModel);
    this.timelineView = value.boardView.boardViewType;
    this.hideLeftPanel = value.hideLeftPanel;
  }

  // Pagination inputs
  @Input() paginationOption?: TUserPaginationOption;
  @Input() paginationInfo?: TPaginationInfo;

  // Toolbard options
  @Input() toolbarActions: TToolbarAction[] = [];

  // Render Type to change configuration
  @Input() configuration: TTimelineConfiguration = {
    renderType: 'TNA_BOARD', // Default
  };

  @Input() isFilterActive = false;

  //Only disabling strip Left click and right click events [PLN-160]
  @Input() disableStripEvents = false;

  @Input() hideOpenInfoBtn = false;

  // Strip drag and drop
  #loadStrip: TRectDropElement[] | null = null;

  @Input() set loadStrip(value: TRectDropElement[] | null) {
    if (value) {
      this.#loadStrip = value;
      this.addStrip();
    } else {
      this.#loadStrip = null;
      this.removeStrip();
    }
  }

  _markers: TTimelineIndicators = { indicators: [] };
  @Input() set markers(value: TTimelineIndicators) {
    this._markers = value;
    this.addMarkers();
  }

  @Input() set viewDateRange(range: TTimelineDateRangeDTO | null) {
    this.#viewDateRange = range;
    if (this.#drawModel) this.#scrollToViewRange();
  }

  // ===================

  // Strip Events
  @Output() stripEvent = new EventEmitter<TEntityActions>();
  @Output() openFilter = new EventEmitter();
  @Output() recalibrate = new EventEmitter<TLL_RECALIBRATE_TYPES>();
  @Output() openInfo = new EventEmitter<Event>();
  @Output() updatedPaginationOption = new EventEmitter<TUserPaginationOption>();

  @Output() onToolbarAction = new EventEmitter<TToolbarActionEventEmit>();
  @Output() onTimelineUpdate = new EventEmitter<TTimelineModel['boardView']>();
  @Output() onTimelineView = new EventEmitter<Event>();
  @Output() onPrev = new EventEmitter<TTimelineModel['boardView']>();
  @Output() onNext = new EventEmitter<TTimelineModel['boardView']>();
  @Output() onToday = new EventEmitter<TTimelineModel['boardView']>();
  @Output() onScrollViewChange = new EventEmitter<TTimelineDateRangeDTO>();

  @ViewChild('timeline') timelineElm!: ElementRef<HTMLDivElement>;

  #resizeObserver: ResizeObserver;
  constructor(
    private modalController: ModalController,
    private popoverController: PopoverController,
    private menuController: MenuController,
  ) {
    this.#resizeObserver = new ResizeObserver((entries) => {
      entries.forEach(() => {
        this.#renderTimeline(this._timelineModel);
      });
    });
  }

  // keyboard arrows
  @HostListener('window:keydown', ['$event'])
  async onKeyPress(event: KeyboardEvent) {
    const hostEl = this.timelineElm?.nativeElement;
    if (!hostEl || !hostEl.isConnected || hostEl.offsetParent === null) return;

    const isModalOpen = await this.modalController.getTop();
    const isPopoverOpen = await this.popoverController.getTop();
    const isMenuOpen = await this.menuController.isOpen();
    if (isModalOpen || isPopoverOpen || isMenuOpen) return;
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.onToolbarAction.emit(['SEARCH', null]);
      return;
    }

    if (event.key === 'ArrowLeft') {
      this.onPrev.emit(this._timelineModel.boardView);
    } else if (event.key === 'ArrowRight') {
      this.onNext.emit(this._timelineModel.boardView);
    }
  }
  //

  removeStrip() {
    if (this.#chartGroup) removeCursor(this.#chartGroup);
  }

  addStrip() {
    if (this.#chartGroup && this.#loadStrip && this.#drawModel) {
      addStripOnCursor(this.#loadStrip, this.#chartGroup, this.#drawModel, (e) => {
        this.stripEvent.emit(e);
      });
    }
  }

  addMarkers() {
    if (this.#chartGroup && this.#drawModel) {
      addMarkers(this.#chartGroup, this.#drawModel, this._markers.indicators);
    }
  }

  changeTimelineView($event: Event) {
    const event = $event as unknown as { detail: { value: 'DAILY' | 'WEEKLY' | 'MONTHLY' } };
    const value = event.detail.value;
    this.onTimelineUpdate.emit({
      ...this._timelineModel.boardView,
      boardViewType: value,
    });
  }

  #renderTimeline(timelineModel: TTimelineModel) {
    // Create Draw Model only if the timeline elm is defined
    if (this.timelineElm && this.timelineElm.nativeElement) {
      const drawModel = TIMELINE_D3.createDrawModel(
        this.timelineElm,
        timelineModel,
        this.configuration,
        this.#viewDateRange ?? undefined,
      );
      this.#drawModel = drawModel;
      this.#chartGroup = createTimelineChart(drawModel, (e) => {
        this.stripEvent.emit(e);
      });
      if (!this.#timelineWrapperScrollEl) {
        this.#timelineWrapperScrollEl = this.timelineElm.nativeElement.querySelector(
          '.timeline-wrapper',
        ) as HTMLDivElement;
      }

      addStripEvent(this.#chartGroup, drawModel, (timelineEventType) => {
        if (!this.disableStripEvents) {
          this.stripEvent.emit(timelineEventType);
        }
      });
      this.addStrip();
      this.addMarkers();
      this.#scrollFirstHighlightedStripIntoView();
      this.#setupScrollRangeTracking();
    }
  }

  #scrollToViewRange() {
    if (!this.#viewDateRange || !this.#drawModel) return;

    requestAnimationFrame(() => {
      if (!this.#timelineWrapperScrollEl || !this.#drawModel || !this.#viewDateRange) return;

      this.#isProgrammaticScrolling = true;
      this.#timelineWrapperScrollEl.scrollLeft = DRAW_MODEL_UTILS.getPostionByDate(
        this.#viewDateRange.from as TDate,
        this.#drawModel,
      );

      setTimeout(() => (this.#isProgrammaticScrolling = false), 50);
    });
  }

  #scrollFirstHighlightedStripIntoView() {
    requestAnimationFrame(() => {
      // Step 1: Scroll horizontally to the date range
      if (this.#viewDateRange && this.#drawModel && this.#timelineWrapperScrollEl) {
        this.#isProgrammaticScrolling = true;
        this.#timelineWrapperScrollEl.scrollLeft = DRAW_MODEL_UTILS.getPostionByDate(
          this.#viewDateRange.from as TDate,
          this.#drawModel,
        );
        setTimeout(() => (this.#isProgrammaticScrolling = false), 50);
      }

      // Step 2: Scroll vertically to the highlighted strip
      const firstHighlightedStrip = this._timelineModel?.entities
        .flatMap((g) => g.entities)
        .flatMap((e) => e.elements)
        .find((el) => el.type === 'STRIP' && el.isStripHighlighted);
      if (firstHighlightedStrip) {
        const stripElement = document.getElementById(firstHighlightedStrip.id);
        if (stripElement) {
          stripElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    });
  }

  #setupScrollRangeTracking() {
    if (this.configuration.renderType !== 'LL_BOARD') return;
    if (!this.#timelineWrapperScrollEl || this.#scrollRangeListener) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    this.#scrollRangeListener = () => {
      if (this.#isProgrammaticScrolling) return;
      if (debounceTimer) clearTimeout(debounceTimer);

      debounceTimer = setTimeout(() => {
        if (!this.#drawModel || !this.#timelineWrapperScrollEl) return;
        const scrollLeft = this.#timelineWrapperScrollEl.scrollLeft;
        const viewportWidth = this.#timelineWrapperScrollEl.clientWidth - this.#drawModel.layout.margin.left;
        const from = DRAW_MODEL_UTILS.getDateByXPosition(scrollLeft, this.#drawModel);
        const to = DRAW_MODEL_UTILS.getDateByXPosition(scrollLeft + viewportWidth, this.#drawModel);
        this.onScrollViewChange.emit({ from, to });
      }, 150);
    };

    this.#timelineWrapperScrollEl.addEventListener('scroll', this.#scrollRangeListener);
  }

  ngAfterViewInit(): void {
    this.#renderTimeline(this._timelineModel);
    this.#resizeObserver.observe(this.timelineElm.nativeElement);
  }

  ngOnDestroy(): void {
    this.#resizeObserver.disconnect();
    if (this.#timelineWrapperScrollEl && this.#scrollRangeListener) {
      this.#timelineWrapperScrollEl.removeEventListener('scroll', this.#scrollRangeListener);
    }
  }

  updateUserPagination($event: TUserPaginationOption) {
    this.paginationOption = $event;
    this.updatedPaginationOption.emit($event);
  }

  print() {
    const mywindow = window.open('', 'PRINT', 'height=400,width=600');

    if (mywindow) {
      mywindow.document.write('<html><head><title>' + document.title + '</title>');
      mywindow.document.write(`<style>
      body {
        font-family: Roboto, "Helvetica Neue", sans-serif;
        padding:0;
        margin:0;
      }
      .header, .left-nav {
        position: absolute !important;
      }
      @media print{@page {size: landscape}}
      .timeline-wrapper {
        overflow: visible !important;
      }
      </style></head><body>`);
      mywindow.document.write(this.timelineElm.nativeElement.innerHTML);
      mywindow.document.write('</body></html>');

      mywindow.print();

      return true;
    }
    return false;
  }
}
